import { ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Collapse, Input, Modal, Space } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router';

import { useSharedState } from '../../../hooks/use-shared-state';
import getUserGuidePath from '../../../utils/user-guide';
import { useUpsertDashboard } from '../dashboard-queries';
import { DashboardUpsert, dashboardListState } from '../models/dashboard-list';

export function DashboardUpsertDialog({ model }: { model: DashboardUpsert }) {
  const [name, setName] = useState<string>(model.name);
  const [nameError, setNameError] = useState<string>('');

  const upsertDashboard = useUpsertDashboard();

  const [, setDashboardList] = useSharedState(dashboardListState);
  const onCancel = () => {
    // updates model.create to undefined to close modal.
    setDashboardList({ ...dashboardListState.get(), upsert: undefined });
  };
  const onOk = async () => {
    if (!name.trim()) {
      setNameError('This field is required.');
      return;
    }

    const res = await upsertDashboard.mutateAsync({ ...model, name });
    if (!res?.ok) {
      // keep the dialog open, the error notification is raised by the mutation.
      return;
    }

    setDashboardList({ ...dashboardListState.get(), upsert: undefined });
  };

  const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setName(evt.target.value);
    setNameError('');
  };

  const docsUrl = getUserGuidePath();

  const { branding } = window.CONFIG;

  return (
    <Modal
      title={model.id ? 'Edit Dashboard' : 'Create Dashboard'}
      maskClosable={false}
      open
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={upsertDashboard.isPending}
      okButtonProps={{ disabled: upsertDashboard.isPending || !name.trim() }}
      cancelButtonProps={{ disabled: upsertDashboard.isPending }}
      data-testid={model.id ? 'edit-dashboard-modal' : 'create-dashboard-modal'}
    >
      <Space direction="vertical" className="w-full">
        <div>{model.id ? 'Edit dashboard:' : 'Create a new dashboard:'}</div>
        <Input
          placeholder="Name"
          value={name}
          maxLength={40}
          minLength={1}
          allowClear
          status={nameError ? 'error' : undefined}
          onPressEnter={onOk}
          onChange={onChange}
          data-testid="dashboard-name-input"
        />
        {nameError && <Alert message={nameError} type="error" />}

        <Collapse
          className="infoCollapse spaced"
          items={[
            {
              key: '1',
              label: (
                <span data-testid="create-new-resource-cluster-info-title">
                  <InfoCircleOutlined style={{ marginRight: '5px' }} />
                  What are Dashboards?
                </span>
              ),
              children: (
                <div>
                  <p>
                    <strong>Dashboards</strong> are graphical container for a set of widgets that you can customize.
                    <br />
                    Each widget contains a preview of your Python web services, Streamlit, Voila or R Shiny apps.
                    <br />
                    <br />
                    Custom dashboards are a quick and efficient way to display and access your most commonly used {branding.longName}
                    elements.
                    <br />
                    You can build and retain multiple dashboards.
                  </p>
                  <p>
                    For further information, please refer to section &quot;<i>Building Dashboards</i>&quot; of the{' '}
                    <Link to={docsUrl} onClick={onCancel}>
                      documentation <ExportOutlined />
                    </Link>
                  </p>
                </div>
              ),
            },
          ]}
        ></Collapse>
      </Space>
    </Modal>
  );
}
